import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export interface ExcelSheet {
	sheetName: string;
	data: Record<string, any>[] | any[][];
	header?: string[] | string[][];
	origin?: string;
}

@Injectable({
	providedIn: 'root'
})

export class ExcelService {

	constructor() { }

	exportExcel(sheets: ExcelSheet[], fileName: string) {
		const workbook: XLSX.WorkBook = XLSX.utils.book_new();

		for (const sheet of sheets) {
			let worksheet: XLSX.WorkSheet;

			const isAOA = Array.isArray(sheet.data?.[0]);
			const headers = this.normalizeHeaders(sheet.header);

			if (headers) {
				worksheet = XLSX.utils.aoa_to_sheet(headers);
				const dataOrigin = sheet.origin || `A${headers.length + 1}`;

				if (sheet.data && sheet.data.length) {
					if (isAOA)
						XLSX.utils.sheet_add_aoa(worksheet, sheet.data as any[][], { origin: dataOrigin });
					else
						XLSX.utils.sheet_add_json(worksheet, sheet.data as Record<string, any>[], { origin: dataOrigin, skipHeader: true });
				}
			} else {
				if (isAOA)
					worksheet = XLSX.utils.aoa_to_sheet(sheet.data as any[][]);
				else
					worksheet = XLSX.utils.json_to_sheet(sheet.data as Record<string, any>[] || []);
			}

			// Auto-width: compute column widths from data and header
			const columnWidths = this.setColumnsWidth(worksheet);
			if (columnWidths && columnWidths.length)
				worksheet['!cols'] = columnWidths;

			XLSX.utils.book_append_sheet(workbook, worksheet, this.checkFileName(sheet.sheetName));
		}

		const workbookArrayBuffer: ArrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
		const workbookBlob = new Blob([workbookArrayBuffer], { type: 'application/octet-stream' });
		saveAs(workbookBlob, fileName);
	}

	// normalize header to 2D array format
	private normalizeHeaders(header?: string[] | string[][]): string[][] | null {
		if (!header || !header.length) return null;
		if (Array.isArray(header[0])) return header as string[][];
		return [header as string[]];
	}

	// sanitize sheet name to satisfy Excel limitations
	private checkFileName(name: string): string {
		if (!name) return 'Sheet';

		const maxLen = 31;
		// max 31 chars and cannot contain : \ / ? * [ ]
		const invalid = /[:\\\/\?\*\[\]]/g;
		const clean = name.replace(invalid, '_').trim();

		const finalName = clean ? (clean.length > maxLen ? clean.substring(0, maxLen) : clean) : 'Sheet';
		return finalName;
	}

	// compute approximate column widths for a worksheet based on cell string lengths
	private setColumnsWidth(worksheet: XLSX.WorkSheet): XLSX.ColInfo[] {
		const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
		const columnInfos: XLSX.ColInfo[] = [];

		const startCol = range.s.c;
		const endCol = range.e.c;
		const startRow = range.s.r;
		const endRow = range.e.r;

		for (let columnIndex = startCol; columnIndex <= endCol; ++columnIndex) {
			let maxColumnChars = 10; // minimum width

			for (let rowIndex = startRow; rowIndex <= endRow; ++rowIndex) {
				const cellRef = XLSX.utils.encode_cell({ c: columnIndex, r: rowIndex });
				const worksheetCell = worksheet[cellRef];
				if (worksheetCell && worksheetCell.v != null) {
					const cellText = String(worksheetCell.v);
					// approximate width: characters + extra padding for non-ASCII (CJK)
					const estimatedChars = cellText.length + (/[ -]/.test(cellText) ? 0 : 2);
					if (estimatedChars > maxColumnChars)
						maxColumnChars = estimatedChars;
				}
			}

			// constrain width between 10 and 50 characters
			columnInfos.push({ wch: Math.min(Math.max(maxColumnChars, 10), 50) });
		}

		return columnInfos;
	}
}
